import { Box, Card, Link } from '@factorialco/deprecated-design-system'

import { parseBackendDate } from 'modules/core/components/DateNavigator/dateFormatUtils'
import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import i18n from 'modules/core/lib/i18n'

import BlankSlate from 'modules/insights/components/BlankSlate'
import { CycleDateRangeWithSetters } from 'modules/insights/hooks/useCycleDateRange'
import { HeadcountWidgetAction } from 'modules/insights/lib/events'
import type HeadcountEvolution from 'modules/insights/lib/metrics/headcountEvolution'

import ChartHeadcountEvolution from './ChartHeadcountEvolution'
import { HeadcountCounters } from './HeadcountCounters'

interface Props {
  cycleDateRange: CycleDateRangeWithSetters
  headcountEvolution: HeadcountEvolution
  onXAxisSelected?: (xAxis: Date, action: HeadcountWidgetAction) => void
}

/**
 * HeadCount Evolution of the company.
 */
const WidgetHeadcountEvolution = ({
  cycleDateRange,
  headcountEvolution,
  onXAxisSelected,
}: Props) => {
  const emptyResult = headcountEvolution.isEmpty()
  const headcountEvolutionData = headcountEvolution.generateHeadcountEvolution()
  const lastRangeString =
    headcountEvolution.headcountEvolution[headcountEvolution.headcountEvolution.length - 1]?.endDate
  const lastRange = lastRangeString ? parseBackendDate(lastRangeString) : undefined

  const handleOpenHeadcountViewForLastMonth = useUnsafeCallback(() => {
    const dataPoints = headcountEvolutionData[0]?.data
    const lastMonthXAxis = dataPoints[dataPoints.length - 1]?.x

    if (lastMonthXAxis) {
      onXAxisSelected?.(lastMonthXAxis, 'details-link')
    }
  }, [headcountEvolutionData, onXAxisSelected])

  return (
    <Card>
      <Card.Title tooltip={i18n.t('insights.widgets.headcount_evolution.tooltip')}>
        {i18n.t('insights.widgets.headcount_evolution.title')}
      </Card.Title>
      <Card.Content>
        {emptyResult ? (
          <BlankSlate />
        ) : (
          <Box flexDirection="column" gap="s16" paddingX="s8" width="full">
            <Box flexDirection="row" gap="s40" width="full" alignItems="center">
              <HeadcountCounters headcountEvolution={headcountEvolution} date={lastRange} />

              <Box minWidth="none" flexGrow>
                <ChartHeadcountEvolution
                  cycle={cycleDateRange.cycle}
                  headcount={headcountEvolutionData}
                  headcountEvolution={headcountEvolution.headcountEvolution}
                  onXAxisSelected={onXAxisSelected}
                />
              </Box>
            </Box>

            <Box alignItems="flexEnd">
              <Link.Touchable onClick={handleOpenHeadcountViewForLastMonth}>
                {i18n.t('insights.headcount_granular_view.link')}
              </Link.Touchable>
            </Box>
          </Box>
        )}
      </Card.Content>
    </Card>
  )
}

export default WidgetHeadcountEvolution
